import { Link } from "@tanstack/react-router";
import { SiteShell } from "@/components/layout/SiteShell";
import { FeaturedSvg } from "@/components/seo/FeaturedSvg";
import { AdSlot } from "@/components/AdSlot";
import {
  type HariPenting,
  HARI_PENTING,
  getHariPentingForMonth,
} from "@/data/hari-penting";
import {
  HARI_CONTENT,
  type HariContent,
} from "@/lib/hari-penting/content";
import {
  todayISO,
  parseISO,
  diffDays,
  calendarDatePath,
  calendarMonthPath,
  MONTH_NAMES_ID,
  DAY_NAMES_ID,
  dayOfWeek,
} from "@/lib/calendar/dates";
import { getHolidaysForYear } from "@/data/holidays";
import { CalendarDays, Sparkles, Clock, BookOpen, Users } from "lucide-react";
import { HARI_ENTITIES, publisherRef } from "@/lib/seo/entities";
import { HariPdfButton } from "@/components/HariPdfButton";
import { eventSchema } from "@/lib/seo/schema";

interface Props {
  entry: HariPenting;
}

/** Generate FAQ from data + content. */
function buildFaq(entry: HariPenting, content: HariContent | undefined, dd: number, mm: number, isHoliday: boolean) {
  const monthName = MONTH_NAMES_ID[mm - 1];
  const baseline = [
    {
      q: `${entry.name} tanggal berapa?`,
      a: `${entry.name} diperingati setiap tanggal ${dd} ${monthName}.`,
    },
    {
      q: `Apakah ${entry.name} libur nasional?`,
      a: isHoliday
        ? `Ya, ${entry.name} merupakan hari libur nasional di Indonesia.`
        : `${entry.name} bukan libur nasional, namun tetap diperingati di seluruh Indonesia sebagai hari peringatan ${entry.scope}.`,
    },
    {
      q: `Mengapa ${entry.name} diperingati pada tanggal ${dd} ${monthName}?`,
      a: content?.history
        ? content.history.split(". ")[0] + "."
        : `Tanggal ${dd} ${monthName} dipilih sebagai peringatan ${entry.name} berdasarkan latar belakang sejarah peristiwa atau tokoh terkait.`,
    },
  ];
  return [...baseline, ...(content?.extraFaq ?? [])];
}

export function HariPentingPage({ entry }: Props) {
  const [mm, dd] = entry.monthDay.split("-").map((n) => parseInt(n, 10));
  const monthName = MONTH_NAMES_ID[mm - 1];
  const todayP = parseISO(todayISO())!;
  const cy = todayP.y;
  const yearsToShow = [cy - 1, cy, cy + 1];
  const content = HARI_CONTENT[entry.slug];

  // Detect if this date is a national holiday in current year (from holidays.ts).
  const cyHolidays = getHolidaysForYear(cy);
  const cyISO = `${cy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  const isHolidayThisYear = cyHolidays.some((h) => h.date === cyISO && h.type === "libur");

  // Countdown vs today.
  const targetISO = todayISO() <= cyISO ? cyISO : `${cy + 1}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  const daysUntil = diffDays(todayISO(), targetISO);
  const targetP = parseISO(targetISO)!;
  const targetDow = dayOfWeek(targetP.y, targetP.m, targetP.d);

  // Related entries.
  const related = (content?.relatedSlugs ?? [])
    .map((s) => HARI_PENTING.find((h) => h.slug === s))
    .filter(Boolean) as HariPenting[];

  // Other days in same month.
  const sameMonth = getHariPentingForMonth(mm).filter((h) => h.slug !== entry.slug);

  // Prev/next sibling within the same month (ordered by day).
  const monthOrdered = getHariPentingForMonth(mm).sort((a, b) => a.monthDay.localeCompare(b.monthDay));
  const idxInMonth = monthOrdered.findIndex((h) => h.slug === entry.slug);
  const prevSibling = idxInMonth > 0 ? monthOrdered[idxInMonth - 1] : null;
  const nextSibling = idxInMonth >= 0 && idxInMonth < monthOrdered.length - 1 ? monthOrdered[idxInMonth + 1] : null;

  const faq = buildFaq(entry, content, dd, mm, isHolidayThisYear);

  return (
    <SiteShell>
      <div className="py-6 md:py-10">
        <nav aria-label="Breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-1">
            <li><Link to={"/" as never} className="hover:text-foreground">Beranda</Link></li>
            <li>/</li>
            <li><Link to={"/hari" as never} className="hover:text-foreground">Hari Penting</Link></li>
            <li>/</li>
            <li className="text-foreground">{entry.name}</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="rounded-2xl border border-border bg-card p-5 md:p-7">
          <div className="flex flex-col items-start gap-5 md:flex-row md:items-center">
            <FeaturedSvg
              variant="holiday"
              title={entry.name}
              subtitle={`${dd} ${monthName} setiap tahun`}
              badge={String(dd)}
              className="w-full max-w-xs md:w-72 md:flex-shrink-0"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold tracking-tight md:text-3xl">
                {entry.name}, {dd} {monthName} {cy}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground md:text-base">
                {content?.tagline ?? `${entry.name} adalah hari peringatan ${entry.scope === "nasional" ? "nasional Indonesia" : "internasional"} yang diperingati setiap tanggal ${dd} ${monthName}.`}
              </p>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-muted px-3 py-1 font-medium">
                  {entry.scope === "nasional" ? "Peringatan Nasional" : "Internasional"}
                </span>
                {isHolidayThisYear && (
                  <span className="rounded-full bg-red-date/15 px-3 py-1 font-medium text-red-date">
                    Libur Nasional
                  </span>
                )}
              </div>
              <div className="mt-4">
                <HariPdfButton defaultSlugs={[entry.slug]} buttonLabel="Download PDF" />
              </div>
            </div>
          </div>
        </header>

        {/* Semantic facts list (microdata-friendly) */}
        <dl className="mt-4 grid gap-3 rounded-xl border border-border bg-card p-4 text-sm sm:grid-cols-3 md:p-5">
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Tanggal</dt>
            <dd className="mt-1 font-medium">{dd} {monthName}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lingkup</dt>
            <dd className="mt-1 font-medium">{entry.scope === "nasional" ? "Nasional Indonesia" : "Internasional"}</dd>
          </div>
          <div>
            <dt className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Status libur {cy}</dt>
            <dd className="mt-1 font-medium">{isHolidayThisYear ? "Libur Nasional" : "Hari kerja"}</dd>
          </div>
        </dl>

        {/* Quick answer + countdown */}
        <section className="mt-6 grid gap-4 md:grid-cols-3">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-4 md:col-span-2">
            <div className="flex items-start gap-3">
              <Sparkles className="mt-0.5 h-5 w-5 text-primary" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Jawaban cepat
                </p>
                <p className="mt-1 font-semibold">
                  {content?.quickAnswer ??
                    `${entry.name} diperingati setiap tanggal ${dd} ${monthName}.`}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Tahun {targetP.y} jatuh pada hari{" "}
                  <Link
                    to={calendarDatePath(targetP.y, targetP.m, targetP.d) as never}
                    className="font-medium text-primary hover:underline"
                  >
                    {DAY_NAMES_ID[targetDow]}, {dd} {monthName} {targetP.y}
                  </Link>.
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Countdown
                </p>
                <p className="mt-1 text-2xl font-bold">
                  {daysUntil === 0 ? "Hari ini!" : `${daysUntil} hari lagi`}
                </p>
                <p className="text-xs text-muted-foreground">menuju {dd} {monthName} {targetP.y}</p>
              </div>
            </div>
          </div>
        </section>

        {/* 3 tahun: lalu / ini / depan */}
        <section className="mt-6 rounded-xl border border-border bg-card p-4 md:p-5">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-semibold">Tahun lalu, tahun ini, tahun depan</h2>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {yearsToShow.map((y) => {
              const dow = dayOfWeek(y, mm, dd);
              const isHol = getHolidaysForYear(y).some(
                (h) => h.date === `${y}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}` && h.type === "libur",
              );
              const tag = y === cy - 1 ? "Tahun lalu" : y === cy ? "Tahun ini" : "Tahun depan";
              const highlight = y === cy;
              return (
                <Link
                  key={y}
                  to={calendarDatePath(y, mm, dd) as never}
                  className={
                    "group rounded-lg border p-3 transition " +
                    (highlight ? "border-primary/40 bg-primary/5" : "border-border bg-background hover:border-primary/40")
                  }
                >
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {tag}
                  </p>
                  <p className="mt-1 text-lg font-bold group-hover:text-primary">
                    {DAY_NAMES_ID[dow]}
                  </p>
                  <p className="text-sm text-muted-foreground">{dd} {monthName} {y}</p>
                  {isHol && (
                    <p className="mt-1 text-xs font-medium text-red-date">Libur Nasional</p>
                  )}
                </Link>
              );
            })}
          </div>
        </section>

        {/* History / Meaning */}
        {(content?.history || content?.meaning) && (
          <section className="mt-8 rounded-xl border border-border bg-card p-5 md:p-6">
            <div className="flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Sejarah & Makna</h2>
            </div>
            <div className="prose prose-sm prose-neutral mt-3 max-w-none dark:prose-invert">
              {content?.history && <p>{content.history}</p>}
              {content?.meaning && <p>{content.meaning}</p>}
            </div>
          </section>
        )}

        {/* Figure */}
        {content?.figure && (
          <section className="mt-6 rounded-xl border border-border bg-card p-5 md:p-6">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Tokoh Kunci</h2>
            </div>
            <div className="mt-3">
              <p className="text-base font-semibold">
                {content.figure.name} · <span className="text-muted-foreground">{content.figure.role}</span>
              </p>
              {content.figure.bio && (
                <p className="mt-2 text-sm text-muted-foreground">{content.figure.bio}</p>
              )}
            </div>
          </section>
        )}

        {/* Traditions */}
        {content?.traditions && content.traditions.length > 0 && (
          <section className="mt-6 rounded-xl border border-border bg-card p-5 md:p-6">
            <h2 className="text-lg font-semibold">Cara Memperingati</h2>
            <ul className="mt-3 space-y-2 text-sm">
              {content.traditions.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-6">
          <AdSlot size="rectangle" position="hari-detail" />
        </div>

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-8">
            <h2 className="text-lg font-semibold">Hari Penting Terkait</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {related.map((r) => {
                const [rmm, rdd] = r.monthDay.split("-").map((n) => parseInt(n, 10));
                return (
                  <Link
                    key={r.slug}
                    to={`/hari/${r.slug}` as never}
                    className="group rounded-lg border border-border bg-card p-3 hover:border-primary"
                  >
                    <p className="text-xs text-muted-foreground">{rdd} {MONTH_NAMES_ID[rmm - 1]}</p>
                    <p className="mt-1 font-semibold group-hover:text-primary">{r.name}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Other days in same month */}
        {sameMonth.length > 0 && (
          <section className="mt-8 rounded-xl border border-border bg-card p-5 md:p-6">
            <h2 className="text-lg font-semibold">Hari peringatan lain di bulan {monthName}</h2>
            {(prevSibling || nextSibling) && (
              <div className="mt-3 grid gap-2 sm:grid-cols-2">
                {prevSibling && (
                  <Link
                    to={`/hari/${prevSibling.slug}` as never}
                    className="rounded-lg border border-border bg-background p-3 text-sm hover:border-primary"
                  >
                    <p className="text-xs text-muted-foreground">← Sebelumnya di {monthName}</p>
                    <p className="font-semibold group-hover:text-primary">
                      {parseInt(prevSibling.monthDay.slice(3), 10)} {monthName} · {prevSibling.name}
                    </p>
                  </Link>
                )}
                {nextSibling && (
                  <Link
                    to={`/hari/${nextSibling.slug}` as never}
                    className="rounded-lg border border-border bg-background p-3 text-right text-sm hover:border-primary sm:col-start-2"
                  >
                    <p className="text-xs text-muted-foreground">Berikutnya di {monthName} →</p>
                    <p className="font-semibold">
                      {parseInt(nextSibling.monthDay.slice(3), 10)} {monthName} · {nextSibling.name}
                    </p>
                  </Link>
                )}
              </div>
            )}
            <ul className="mt-4 grid gap-1.5 text-sm sm:grid-cols-2">
              {sameMonth.map((h) => {
                const hdd = parseInt(h.monthDay.slice(3), 10);
                const dow = dayOfWeek(cy, mm, hdd);
                return (
                  <li key={h.slug}>
                    <Link to={`/hari/${h.slug}` as never} className="hover:text-primary">
                      <span className="text-muted-foreground">{hdd} · {DAY_NAMES_ID[dow].slice(0, 3)}</span>{" "}
                      <span className="font-medium">{h.name}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            <div className="mt-4">
              <Link
                to={calendarMonthPath(cy, mm) as never}
                className="text-sm text-primary hover:underline"
              >
                Lihat kalender lengkap {monthName} {cy} →
              </Link>
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="mt-8 rounded-xl border border-border bg-card p-5 md:p-6">
          <h2 className="text-lg font-semibold">Tanya Jawab</h2>
          <div className="mt-4 space-y-4 text-sm">
            {faq.map((f, i) => (
              <div key={i}>
                <p className="font-semibold">{f.q}</p>
                <p className="mt-1 text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </SiteShell>
  );
}

/** Helper exported for route head() — builds JSON-LD blob list. */
export function buildHariJsonLd(entry: HariPenting, origin: string, cy: number) {
  const [mm, dd] = entry.monthDay.split("-").map((n) => parseInt(n, 10));
  const monthName = MONTH_NAMES_ID[mm - 1];
  const isoThisYear = `${cy}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
  const content = HARI_CONTENT[entry.slug];
  const url = `${origin}/hari/${entry.slug}/`;
  const isHoliday = getHolidaysForYear(cy).some(
    (h) => h.date === isoThisYear && h.type === "libur",
  );
  const faq = buildFaq(entry, content, dd, mm, isHoliday);
  const entity = HARI_ENTITIES[entry.slug];
  const keywords = [entry.name, ...(entry.aliases ?? []), entity?.name].filter(Boolean).join(", ");
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Beranda", item: `${origin}/` },
        { "@type": "ListItem", position: 2, name: "Hari Penting", item: `${origin}/hari/` },
        { "@type": "ListItem", position: 3, name: entry.name, item: url },
      ],
    },
    {
      ...eventSchema(
        {
          name: entry.name,
          date: isoThisYear,
          description:
            content?.tagline ??
            `${entry.name} diperingati setiap tanggal ${dd} ${monthName}.`,
          url: `/hari/${entry.slug}/`,
        },
        "id",
      ),
      ...(entity ? { about: entity } : {}),
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: `${entry.name}: Tanggal, Sejarah, dan Makna`,
      description: content?.tagline ?? `Detail ${entry.name}: tanggal, sejarah, dan tradisi.`,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: publisherRef(origin),
      publisher: publisherRef(origin),
      inLanguage: "id-ID",
      keywords,
      isPartOf: { "@type": "WebSite", url: `${origin}/`, name: "Hari.co.id" },
      ...(entity ? { about: entity, mentions: [entity] } : {}),
    },
  ];
}